import { Badge, Button, HStack, Input, SegmentGroup, Stack, Text } from '@chakra-ui/react'
import { useState } from 'react'

export type PaywallTermOption = {
  id: string
  label: string
  savingsLabel?: string
}

export type PaywallOfferControlsProps = {
  terms: PaywallTermOption[]
  selectedTermId: string
  onTermChange: (termId: string) => void
  appliedCouponCode?: string | null
  couponError?: string | null
  couponIsApplying?: boolean
  onApplyCoupon?: (code: string) => void
  onRemoveCoupon?: () => void
  /** `center` sits the term picker and code link together under a centered heading. */
  align?: 'start' | 'center'
}

/** Controlled selections. Only a caller's resolved coupon is displayed as applied. */
export const PaywallOfferControls = ({
  align = 'start',
  appliedCouponCode,
  couponError,
  couponIsApplying = false,
  onApplyCoupon,
  onRemoveCoupon,
  onTermChange,
  selectedTermId,
  terms,
}: PaywallOfferControlsProps) => {
  const [draftCode, setDraftCode] = useState('')
  // An always-open code box invites leaving checkout to hunt for codes; members with one know to look.
  const [codeRequested, setCodeRequested] = useState(false)
  const codeOpen = codeRequested || Boolean(couponError) || couponIsApplying

  return (
    <Stack
      align={{ base: 'stretch', sm: 'center' }}
      columnGap={6}
      direction={{ base: 'column', sm: 'row' }}
      justify={align === 'center' ? 'center' : 'space-between'}
      rowGap={3}
      wrap={'wrap'}
    >
      {terms.length > 1 ? (
        <SegmentGroup.Root
          aria-label={'Billing period'}
          minW={0}
          size={'lg'}
          value={selectedTermId}
          w={{ base: 'full', sm: 'auto' }}
          onValueChange={details => {
            if (details.value) {
              onTermChange(details.value)
            }
          }}
        >
          <SegmentGroup.Indicator />
          {terms.map(term => (
            <SegmentGroup.Item
              key={term.id}
              flex={{ base: 1, sm: 'initial' }}
              h={'auto'}
              minH={11}
              px={{ base: 2, sm: 4 }}
              py={1.5}
              value={term.id}
            >
              <SegmentGroup.ItemText>
                <Stack align={'center'} gap={0}>
                  <Text fontWeight={'medium'} textStyle={'sm'}>
                    {term.label}
                  </Text>
                  {term.savingsLabel ? (
                    <Text color={'green.fg'} fontWeight={'medium'} lineHeight={'short'} textStyle={'2xs'}>
                      {term.savingsLabel}
                    </Text>
                  ) : null}
                </Stack>
              </SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          ))}
        </SegmentGroup.Root>
      ) : null}
      {onApplyCoupon ? (
        <Stack align={align === 'center' ? 'center' : undefined} gap={2}>
          {appliedCouponCode || codeOpen ? (
            <Text color={'fg.muted'} textStyle={'label2xs'}>
              Promotion code
            </Text>
          ) : null}
          {appliedCouponCode ? (
            <HStack gap={2} wrap={'wrap'}>
              <Badge colorPalette={'green'} variant={'surface'}>
                Code {appliedCouponCode} applied
              </Badge>
              {onRemoveCoupon ? (
                <Button size={'xs'} variant={'ghost'} onClick={onRemoveCoupon}>
                  Remove code
                </Button>
              ) : null}
            </HStack>
          ) : !codeOpen ? (
            <Button
              _hover={{ color: 'fg', textDecorationColor: 'currentColor' }}
              alignSelf={align === 'center' ? 'center' : 'flex-start'}
              color={'fg.muted'}
              h={'auto'}
              px={0}
              size={'xs'}
              textDecoration={'underline'}
              textDecorationColor={'border.emphasized'}
              textUnderlineOffset={'3px'}
              variant={'plain'}
              onClick={() => setCodeRequested(true)}
            >
              Have a promotion code?
            </Button>
          ) : (
            <HStack
              align={'start'}
              as={'form'}
              gap={2}
              justify={align === 'center' ? 'center' : undefined}
              wrap={'wrap'}
              onSubmit={event => {
                event.preventDefault()
                const code = draftCode.trim()
                if (code) {
                  onApplyCoupon(code)
                }
              }}
            >
              {/* A set width, not a cap: in a shrink-to-fit row a capped field grew past the button and wrapped it. */}
              <Input
                aria-invalid={Boolean(couponError)}
                aria-label={'Promotion code'}
                autoFocus={codeRequested}
                maxW={'full'}
                placeholder={'Enter code'}
                size={'sm'}
                value={draftCode}
                w={'15rem'}
                onChange={event => setDraftCode(event.target.value)}
              />
              <Button
                disabled={!draftCode.trim() || couponIsApplying}
                size={'sm'}
                type={'submit'}
                variant={'outline'}
              >
                {couponIsApplying ? 'Checking…' : 'Apply'}
              </Button>
            </HStack>
          )}
          {couponError ? (
            <Text color={'red.fg'} role={'alert'} textStyle={'xs'}>
              {couponError}
            </Text>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  )
}
